import React, { useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { SEO } from './SEO';
import { 
  DollarSign, TrendingUp, Clock, Users, 
  CheckCircle, Trophy, Share2, 
  Wallet, Star
} from 'lucide-react';
import { Button } from './ui/button';
import { openBilling, getBillingUrl } from '../lib/referral';

export const AffiliatePage = () => {
  const { t } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { icon: DollarSign, value: t('affiliate.stats.commission'), label: t('affiliate.stats.commissionLabel') },
    { icon: Clock, value: t('affiliate.stats.duration'), label: t('affiliate.stats.durationLabel') },
    { icon: TrendingUp, value: t('affiliate.stats.payout'), label: t('affiliate.stats.payoutLabel') },
    { icon: Wallet, value: t('affiliate.stats.minimum'), label: t('affiliate.stats.minimumLabel') }
  ];

  const benefits = [
    {
      icon: DollarSign,
      title: t('affiliate.why.commission.title'),
      description: t('affiliate.why.commission.desc')
    },
    {
      icon: Clock,
      title: t('affiliate.why.duration.title'),
      description: t('affiliate.why.duration.desc')
    },
    {
      icon: TrendingUp,
      title: t('affiliate.why.renewals.title'),
      description: t('affiliate.why.renewals.desc')
    },
    {
      icon: Wallet,
      title: t('affiliate.why.payout.title'),
      description: t('affiliate.why.payout.desc')
    }
  ];

  const steps = [
    { icon: Users, title: t('affiliate.howItWorks.step1.title'), desc: t('affiliate.howItWorks.step1.desc') },
    { icon: Share2, title: t('affiliate.howItWorks.step2.title'), desc: t('affiliate.howItWorks.step2.desc') },
    { icon: DollarSign, title: t('affiliate.howItWorks.step3.title'), desc: t('affiliate.howItWorks.step3.desc') },
    { icon: Wallet, title: t('affiliate.howItWorks.step4.title'), desc: t('affiliate.howItWorks.step4.desc') }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO page="affiliate" />
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-blue-950 via-blue-800 to-blue-950 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/20 border border-blue-400/30 rounded-full text-blue-100 text-sm font-semibold mb-6">
              <Star className="w-4 h-4 fill-current" />
              <span>{t('affiliate.hero.badge')}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('affiliate.hero.title')}
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto mb-8">
              {t('affiliate.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => {
                  const lang = window.location.pathname.startsWith('/ru') ? 'ru' : 'en';
                  openBilling('register', lang);
                }}
                className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8 py-6 text-lg"
              >
                {t('affiliate.hero.joinNow')}
              </Button>
              <Button 
                onClick={() => document.getElementById('how-it-works').scrollIntoView({ behavior: 'smooth', block: 'start' })}
                className="bg-blue-800 text-white hover:bg-blue-900 font-semibold px-8 py-6 text-lg"
              >
                {t('affiliate.hero.learnMore')}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-xl mb-4">
                  <stat.icon className="w-8 h-8 text-blue-700" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('affiliate.why.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('affiliate.why.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-blue-500 transition-colors">
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <benefit.icon className="w-7 h-7 text-blue-700" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('affiliate.howItWorks.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('affiliate.howItWorks.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="text-center">
                  <div className="relative inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full mb-4">
                    <step.icon className="w-10 h-10 text-white" />
                    <div className="absolute -top-2 -right-2 w-8 h-8 bg-blue-700 rounded-full flex items-center justify-center text-white font-bold text-sm">
                      {index + 1}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600">{step.desc}</p>
                </div>
                {index < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-10 left-1/2 w-full h-0.5 bg-blue-200" style={{ width: 'calc(100% + 2rem)', marginLeft: '1rem' }}></div>
                )}
              </div>
            ))}
          </div>

          {/* Image */}
          <div className="mt-12 rounded-2xl overflow-hidden max-w-4xl mx-auto">
            <img src="/images/locations/pexels_5716001.jpg" 
              alt="How It Works" loading="lazy"
              className="w-full h-80 object-cover"
 loading="lazy"/>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('affiliate.comparison.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('affiliate.comparison.subtitle')}
            </p>
          </div>

          <div className="bg-white rounded-2xl overflow-hidden border-2 border-gray-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">{t('affiliate.comparison.tableHeaders.program')}</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">{t('affiliate.comparison.tableHeaders.commission')}</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">{t('affiliate.comparison.tableHeaders.cookie')}</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">{t('affiliate.comparison.tableHeaders.renewals')}</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">{t('affiliate.comparison.tableHeaders.payout')}</th>
                    <th className="px-6 py-4 text-left text-sm font-bold text-gray-900">{t('affiliate.comparison.tableHeaders.speed')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {/* NetGrid Host Row */}
                  <tr className="bg-blue-50">
                    <td className="px-6 py-4 font-bold text-blue-700 flex items-center gap-2">
                      <Trophy className="w-5 h-5" />
                      {t('affiliate.comparison.netgrid')}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 text-blue-700 font-bold">
                        <CheckCircle className="w-4 h-4" /> 20%
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 text-blue-700 font-bold">
                        <CheckCircle className="w-4 h-4" /> {t('affiliate.stats.duration')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 text-blue-700 font-bold">
                        <CheckCircle className="w-4 h-4" /> {t('affiliate.comparison.yes')}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-blue-700 font-bold">USDT TRC20</span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-1 text-blue-700 font-bold">
                        <CheckCircle className="w-4 h-4" /> {t('affiliate.comparison.hours')}
                      </span>
                    </td>
                  </tr>
                  
                  {/* Competitor Rows */}
                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-600">{t('affiliate.comparison.competitor1')}</td>
                    <td className="px-6 py-4 text-gray-600">20%</td>
                    <td className="px-6 py-4 text-gray-600">3 {t('affiliate.comparison.months')}</td>
                    <td className="px-6 py-4 text-gray-600">{t('affiliate.comparison.limited')}</td>
                    <td className="px-6 py-4 text-gray-600">PayPal</td>
                    <td className="px-6 py-4 text-gray-600">{t('affiliate.comparison.slowPayout')}</td>
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-600">{t('affiliate.comparison.competitor2')}</td>
                    <td className="px-6 py-4 text-gray-600">15%</td>
                    <td className="px-6 py-4 text-gray-600">30 {t('affiliate.comparison.days')}</td>
                    <td className="px-6 py-4 text-gray-600">{t('affiliate.comparison.no')}</td>
                    <td className="px-6 py-4 text-gray-600">Bank Wire</td>
                    <td className="px-6 py-4 text-gray-600">{t('affiliate.comparison.verySlow')}</td>
                  </tr>

                  <tr className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-gray-600">{t('affiliate.comparison.competitor3')}</td>
                    <td className="px-6 py-4 text-gray-600">20%</td>
                    <td className="px-6 py-4 text-gray-600">60 {t('affiliate.comparison.days')}</td>
                    <td className="px-6 py-4 text-gray-600">{t('affiliate.comparison.no')}</td>
                    <td className="px-6 py-4 text-gray-600">PayPal</td>
                    <td className="px-6 py-4 text-gray-600">{t('affiliate.comparison.slowPayout')}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* Payment Terms */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('affiliate.terms.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Условия выплат */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-200 h-full">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Wallet className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{t('affiliate.terms.withdrawalTitle')}</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{t('affiliate.terms.minimum')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{t('affiliate.terms.fee')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{t('affiliate.terms.method')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                      <span>{t('affiliate.terms.timing')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Начисление */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-8 border-2 border-blue-200 h-full">
              <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{t('affiliate.terms.accrualTitle')}</h3>
                  <ul className="space-y-3 text-gray-700">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                      <span>{t('affiliate.terms.monthlyAccrual')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                      <span>{t('affiliate.terms.commission60')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                      <span>{t('affiliate.terms.renewals12')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-blue-700 flex-shrink-0 mt-0.5" />
                      <span>{t('affiliate.terms.realtime')}</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 text-center">
            <p className="text-gray-600">{t('affiliate.terms.support')}</p>
            <p className="mt-4">
              <a
                href={(typeof window !== 'undefined' && window.location.pathname.startsWith('/ru') ? '/ru' : '') + '/affiliate-terms'}
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-blue-50 hover:bg-blue-100 text-blue-700 font-semibold transition-colors border border-blue-100"
              >
                <Star className="w-4 h-4" />
                {typeof window !== 'undefined' && window.location.pathname.startsWith('/ru')
                  ? 'Правила партнёрской программы'
                  : 'Affiliate Program Rules'}
              </a>
            </p>
          </div>

          {/* Money Image */}
          <div className="mt-12 rounded-2xl overflow-hidden">
            <img src="/images/locations/unsplash_1692598504840-26e774f6dd62.jpg" 
              alt="Money" loading="lazy"
              className="w-full h-64 object-cover"
 loading="lazy"/>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-700 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            {t('affiliate.cta.title')}
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            {t('affiliate.cta.subtitle')}
          </p>
          <Button 
            onClick={() => {
              const lang = window.location.pathname.startsWith('/ru') ? 'ru' : 'en';
              openBilling('register', lang);
            }}
            className="bg-white text-blue-700 hover:bg-blue-50 font-semibold px-8 py-6 text-lg"
          >
            {t('affiliate.cta.button')}
          </Button>

          {/* Partnership Image */}
          <div className="mt-12 rounded-2xl overflow-hidden max-w-3xl mx-auto">
            <img src="/images/locations/pexels_3184423.jpg" 
              alt="Partnership" loading="lazy"
              className="w-full h-64 object-cover"
 loading="lazy"/>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};


